import Tabs from "../../components/General/ResponsiveTabs/ResponsiveTabs";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Tab, CircularProgress, Typography, Button } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../hooks/useAuth";
import axios from "axios";
import { apiVariable } from "../../api/apiVariable";

import AssetRepairs from "../../components/Fleet/AssetRepairs/AssetRepairs";
import AssetInformation, {
  fetchAsset,
} from "../../components/Fleet/AssetInformation/AssetInformation";
import AssetMaintenanceOperations from "../../components/Fleet/AssetMaintenanceOperations/AssetMaintenanceOperations";
import YardRepairs from "../../components/Fleet/YardRepairs/YardRepairs";
import LoanInformation from "../../components/Fleet/LoanInformation/LoanInformation";
import RentalInformation from "../../components/Fleet/RentalInformation/RentalInformation";
import GenerateRepairsButton from "../../components/Fleet/GeneratePdfButtons/GenerateRepairsButton/GenerateRepairsButton";
import GenerateMaintenanceButton from "../../components/Fleet/GeneratePdfButtons/GenerateMaintenance/GenerateMaintenanceButton";
import GenerateMaintenancePerMileButton from "../../components/Fleet/GeneratePdfButtons/GenerateMaintenancePerMileButton/GenerateMaintenancePerMileButton";
import AssignDriverButton from "../../components/Fleet/AssignDriverButton/AssignDriverButton";
import AssignTrailerButton from "../../components/Fleet/AssignTrailerButton/AssignTrailerButton";
import UpdateAssetPopup from "../../components/Fleet/UpdateAssetPopup/UpdateAssetPopup";
import SaleStatus from "../../components/Fleet/SaleStatus/SaleStatus";
import Documents from "../../components/Fleet/Documents/Documents";
import AssignedDrivers from "../../components/Fleet/AssignedDrivers/AssignedDrivers";
import AssignedTrailers from "../../components/Fleet/AssignedTrailers/AssignedTrailers";
import PaymentHistories from "../../components/Fleet/PaymentHistories/PaymentHistories";
import RentalHistory from "../../components/Fleet/RentalHistory/RentalHistory";
import AssetNavigation from "../../components/Fleet/AssetNavigation/AssetNavigation";
import TruckAccidentsTable from "../../components/Fleet/TruckAccidentsTable/TruckAccidentsTable";
import ShareAssetDocumentsButton from "../../components/Fleet/ShareAssetDocumentsButton/ShareAssetDocumentsButton";

const fetchCompanyHistory = async (
  token: string | null | undefined,
  truckId: string,
) => {
  const { data } = await axios.get(
    `${apiVariable}/ownerCompany/${truckId}/truckCompanyHistory`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
};

const TruckPage = () => {
  const { operationId, id } = useParams<{ id: string; operationId?: string }>();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const { token } = useAuth();

  useEffect(() => {
    if (operationId) {
      setSelectedTab(1);
    }
  }, [operationId]);

  const {
    data: asset,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["asset", id, "truck"],
    queryFn: () => fetchAsset(token, id, "truck"),
  });

  const { data: companyHistory } = useQuery({
    queryKey: ["companyHistory", id],
    queryFn: () => fetchCompanyHistory(token, id as string),
    enabled: !!id,
  });

  const distinctCompanies = useMemo(() => {
    if (!companyHistory) return [];

    const seen = new Set<string>();
    return companyHistory
      .filter((c: { companyId: string }) => {
        if (seen.has(c.companyId)) return false;
        seen.add(c.companyId);
        return true;
      })
      .map((c: { companyId: any; name: any }) => ({
        id: c.companyId,
        label: c.name,
      }));
  }, [companyHistory]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  if (isLoading) {
    return (
      <Box textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">Error: {(error as Error).message}</Typography>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <AssetNavigation currentId={id as string} assetType="truck" />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "center",
          mb: 3,
        }}
      >
        <GenerateMaintenanceButton id={id} token={token} assetType="truck" />
        <GenerateRepairsButton id={id} token={token} assetType="truck" />
        <GenerateMaintenancePerMileButton id={id} token={token} />
        <AssignDriverButton />
        <AssignTrailerButton buttonLocation="truck" />
        <ShareAssetDocumentsButton
          assetType="truck"
          folderId={asset.folderId}
          asset={asset.truck}
        />
        <Button
          variant="outlined"
          onClick={() =>
            navigate(
              `/operations/planner?${new URLSearchParams({
                assetType: "truck",
                assetId: id ?? "",
                unitNumber: asset.truck?.unitNumber ?? "",
              }).toString()}`,
            )
          }
        >
          Show on planner
        </Button>
        {!isLoading && (
          <UpdateAssetPopup asset={asset.truck} assetType="truck" id={id} />
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
          justifyContent: "center",
          alignItems: "stretch",
          gap: 3,
          mb: 4,
        }}
      >
        <Box
          sx={{
            flex: 1,
            border: "1px solid #ccc",
            borderRadius: 2,
            p: 2,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <LoanInformation
            asset={asset.loan}
            isLoading={isLoading}
            isError={isError}
            error={error}
          />
          <SaleStatus
            asset={asset.sale}
            isLoading={isLoading}
            isError={isError}
            error={error}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            border: "1px solid #ccc",
            borderRadius: 2,
            p: 2,
            minWidth: 0,
          }}
        >
          <AssetInformation
            asset={asset.truck}
            isLoading={isLoading}
            isError={isError}
            error={error}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            border: "1px solid #ccc",
            borderRadius: 2,
            p: 2,
            minWidth: 0,
          }}
        >
          <RentalInformation
            asset={asset.rent}
            isLoading={isLoading}
            isError={isError}
            error={error}
          />
        </Box>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Tabs value={selectedTab} onChange={handleTabChange} centered>
          <Tab label="Asset Repairs" />
          <Tab label="Asset Maintenance Operations" />
          <Tab label="Yard Repairs" />
          <Tab label="Assigned Drivers" />
          <Tab label="Assigned Trailers" />
          <Tab label="Rental History" />
          <Tab label="Documents" />
          <Tab label="Payment Histories" />
          <Tab label="Accidents" />
        </Tabs>

        {selectedTab === 0 && <AssetRepairs assetType="truck" />}
        {selectedTab === 1 && (
          <AssetMaintenanceOperations
            assetType="truck"
            operationId={operationId}
          />
        )}
        {selectedTab === 2 && <YardRepairs assetType="truck" />}
        {selectedTab === 3 && (
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <AssignedDrivers truckId={id as string} token={token} />
          </Box>
        )}
        {selectedTab === 4 && (
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <AssignedTrailers truckId={id as string} token={token} />
          </Box>
        )}
        {selectedTab === 5 && (
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <RentalHistory
              assetId={id as string}
              token={token}
              assetType="truck"
            />
          </Box>
        )}
        {selectedTab === 6 && (
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <Documents
              folderId={asset.folderId}
              token={token}
              assetType="truck"
              unitNumber={asset.truck?.unitNumber}
              distinctCompanies={distinctCompanies}
            />
          </Box>
        )}
        {selectedTab === 7 && (
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <PaymentHistories
              assetId={id as string}
              token={token}
              assetType="truck"
            />
          </Box>
        )}
        {selectedTab === 8 && (
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              justifyContent: "center",
              mt: 2,
            }}
          >
            <TruckAccidentsTable truckId={id as string} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TruckPage;
